<template>
  <section class="RunManageButtonView">
    <FormButton
      v-bind="{orientation}"
      iconName="pencil"
      :disabled="isBusy"
      @click="isMenuVisible = true"
    >
      Manage run
    </FormButton>

    <Teleport to="body">
      <Transition>
        <MenuModal
          v-if="isMenuVisible"
          data-testid="manageRunMenuModal"
          :aria-busy="isBusy"
          @close="handleMenuClose"
        >
          <MenuModalButton
            v-for="item in menuItems"
            :key="item.id"
            v-bind="item"
            :disabled="isBusy"
            @click="handleMenuItemClick(item)"
          />
        </MenuModal>
      </Transition>
    </Teleport>
  </section>
</template>

<script setup lang="ts">
import FormButton from "@/components/FormButton.vue";
import { ModalMenuItem } from "@/components/MenuModal.types";
import MenuModal from "@/components/MenuModal.vue";
import MenuModalButton from "@/components/MenuModalButton.vue";
import { supabaseClient } from "@/lib/database/supabase";
import { RouteName } from "@/lib/router/routes";
import useModalStore from "@/stores/useModalStore";
import useRunCarouselStore from "@/stores/useRunCarouselStore";
import useRunStore from "@/stores/useRunStore";
import useSeasonStore from "@/stores/useSeasonStore";
import { RunSummary } from "@shared/lib/database/query.types";
import { computed, ref } from "vue";

const emit = defineEmits<{
  delete: [],
}>();

const {
  run,
} = defineProps<{
  run: RunSummary,
  orientation?: "horizontal" | "vertical",
}>();

const {
  openModal,
} = useModalStore();

const {
  updateRun,
  removeRun,
} = useRunStore();

const {
  updateRun: updateCarouselRun,
  removeRun: removeCarouselRun,
} = useRunCarouselStore();

const {
  updateSeasonRun,
  removeSeasonRun,
} = useSeasonStore();

const isMenuVisible = ref(false);
const isCanceling = ref(false);
const isDeleting = ref(false);

const menuItems = computed((): Array<ModalMenuItem> => {
  return [
    {
      id: "edit",
      iconName: "pencil",
      title: "Edit run",
      subtitle: "Change the time, court, etc.",
    },
    {
      id: "cancel",
      iconName: "whistle",
      ...cancelLabel.value,
    },
    {
      id: "delete",
      iconName: "trash",
      title: isDeleting.value ? "Deleting run…" : "Delete run",
      subtitle: "Remove the run from the schedule",
    },
  ];
});

const isBusy = computed(() => {
  return isCanceling.value || isDeleting.value;
});

const cancelLabel = computed(() => {
  if (!run.canceled_at && isCanceling.value) return {
    title: "Canceling run…",
    subtitle: "Mark as canceled & prevent updates",
  };
  if (run.canceled_at && !isCanceling.value) return {
    title: "Restore run",
    subtitle: "Mark as active & allow updates",
  };
  if (run.canceled_at && isCanceling.value) return {
    title: "Restoring run…",
    subtitle: "Mark as active & allow updates",
  };

  return {
    title: "Cancel run",
    subtitle: "Mark as canceled & prevent updates",
  };
});

function handleMenuItemClick (item: ModalMenuItem) {
  switch (item.id) {
    case "edit":
      isMenuVisible.value = false;
      openModal({
        name: RouteName.runEdit,
        params: { runId: run.id },
      });
      break;
    case "cancel":
      handleCancelRunClick();
      break;
    case "delete":
      handleDeleteRunClick();
      break;
  }
}

async function handleCancelRunClick () {
  isCanceling.value = true;

  const {
    data: updatedRun,
    error,
  } = await supabaseClient
    .from("runs")
    .update({ canceled_at: run.canceled_at ? null : new Date().toISOString() })
    .eq("id", run.id)
    .select("canceled_at")
    .maybeSingle();

  if (error) {
    alert(error.message);
  } else if (!updatedRun) {
    alert("You don’t have permission to cancel this run.");
  } else if (updatedRun) {
    isMenuVisible.value = false;

    // TODO: DRY
    updateRun(run.id, { canceled_at: updatedRun.canceled_at });
    updateCarouselRun(run.id, updatedRun);
    if (run.season_id) updateSeasonRun(run.season_id, run.id, updatedRun);
  }

  isCanceling.value = false;
}

async function handleDeleteRunClick () {
  isDeleting.value = true;

  if (!confirm("Are you sure you want to delete this run?\n\nYou can’t run this back.")) {
    isDeleting.value = false;
    return;
  }

  const {
    count,
    error,
  } = await supabaseClient
    .from("runs")
    .delete({ count: "exact" })
    .eq("id", run.id);

  if (error) {
    alert(error.message);
  } else if (count === 0) {
    alert("You don’t have permission to delete this run.");
  } else {
    isMenuVisible.value = false;

    removeRun(run.id);
    removeCarouselRun(run.id);
    if (run.season_id) removeSeasonRun(run.season_id, run.id);
    emit("delete");
  }

  isDeleting.value = false;
}

function handleMenuClose () {
  if (isBusy.value) return;

  isMenuVisible.value = false;
}
</script>
